import { useEffect, useRef, useState } from "react";
import { Bell, Building2, Ship, Satellite, X } from "lucide-react";
import { timeAgo } from "@/lib/maritime";

const REASON = {
  rig: { icon: Building2, color: "#F59E0B", label: "Near oil rig" },
  vessel: { icon: Ship, color: "#FF6B9D", label: "Near AIS vessel" },
  class: { icon: Satellite, color: "#22d3ee", label: "Vessel/infra origin" },
};

export const AlertsBell = ({ alerts = [], onAlertClick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const count = alerts.length;

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="alerts-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-300 hover:text-[#FF6B9D] hover:border-[#FF2A6D]/40 transition-colors"
      >
        <Bell className="h-4.5 w-4.5" />
        {count > 0 && (
          <span
            data-testid="alerts-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#FF2A6D] text-white text-[10px] font-mono font-bold tabular-nums"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alerts-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[70vh] flex flex-col rounded-xl bg-slate-900/97 backdrop-blur-xl border border-[#FF2A6D]/25 shadow-2xl overflow-hidden st-fade z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/80">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-[#FF6B9D]" />
              <span className="font-display text-sm font-bold text-white">Slick Alerts</span>
              <span className="font-mono text-[11px] text-slate-500">{count}</span>
            </div>
            <button
              data-testid="alerts-close"
              onClick={() => setOpen(false)}
              className="text-slate-500 hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="px-4 py-2 text-[10px] font-mono text-slate-500 border-b border-slate-800/50">
            Fresh, high-confidence detections near a rig or vessel
          </p>

          <div className="flex-1 overflow-y-auto st-scroll">
            {count === 0 && (
              <p data-testid="alerts-empty" className="text-center text-xs text-slate-500 py-8 font-mono">
                No active alerts.
              </p>
            )}
            {alerts.map((a) => {
              const r = REASON[a.reason] || REASON.class;
              const Icon = r.icon;
              const detail =
                a.reason === "rig" && a.near_rig
                  ? `${a.near_rig.field || "Rig"} · ${a.near_rig.distance_km} km`
                  : a.reason === "vessel" && a.near_vessel
                  ? `MMSI ${a.near_vessel.mmsi}`
                  : r.label;
              return (
                <button
                  key={a.cerulean_id}
                  data-testid={`alert-item-${a.cerulean_id}`}
                  onClick={() => {
                    onAlertClick?.(a);
                    setOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: r.color }} />
                    <span className="text-sm text-slate-200 font-medium truncate flex-1">{detail}</span>
                    <span className="font-mono text-[11px] tabular-nums" style={{ color: r.color }}>
                      {Math.round((a.machine_confidence || 0) * 100)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 pl-5.5">
                    <span className="font-mono text-[10px] text-slate-500">{a.cls_name || "Slick"}</span>
                    <span className="font-mono text-[10px] text-slate-600">·</span>
                    <span className="font-mono text-[10px] text-slate-500">{timeAgo(a.image_ts)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsBell;
